import {
  filingDestinationPhrase,
  type FilingDestinationModel,
} from '@/hooks/finance/usePortalUploadModel';

/** The chip word plus the sentence beneath it, for a read-only money row's lock banner. */
export interface RowLockCopy {
  /** The short, bold lock signal rendered as a chip. */
  label: string;
  /** The sentence explaining the lock and, where there is one, the remedy. */
  detail: string;
  /** The label for the link to the report to reopen from; absent when there is none to offer. */
  reopenLinkLabel?: string;
}

/**
 * Jurisdiction-aware copy for the read-only lock banner shown at the top of a frozen money
 * row's expanded detail (a Contribution/Expenditure the treasurer cannot edit).
 *
 * Two different facts freeze a row, and they carry DIFFERENT meaning — the banner must not
 * collapse them (GitHub issue #659):
 *
 *   - **Upload-locked** (`uploadLocked`) — the row is a live member of an uploaded export batch.
 *     This is a genuine filing fact: the money really is sitting with the regulator, so the copy
 *     names the campaign's own destination and says it is never edited.
 *       - Portal-upload jurisdictions (AL FCPA today, VA COMET, …) name their portal.
 *       - Federal (`certifyAtGenerate`, #482) and any non-portal filer stay jurisdiction-neutral
 *         ("already on file"): federal files at efo.fec.gov (no nameable state portal) and the
 *         FEC-copy discipline forbids "FEC" here.
 *
 *   - **Period-locked only** (ADR-019) — the period is reported history, but the row itself was
 *     not uploaded, so the banner cannot name where it was filed. A missed deadline alone never
 *     reaches here: an overdue, never-filed period stays open for the late filer.
 *
 *   - **Period-locked with a filed report to reopen from** (`reopenable`) — a filed in-app report
 *     covers the row's date and the viewer can manage finance, so the banner names the remedy:
 *     reopen the period from that report, whose page holds the Reopen action.
 *
 * Without `reopenable`, that second branch deliberately states neither the CAUSE nor a remedy,
 * because it cannot know either. The lock is a disjunction — a report for the period is filed and
 * its deadline has passed, OR the period sits at/below the ADR-018 prior-filing floor (history
 * filed before onboarding).
 * Naming the deadline would be false for a floor-locked row, and offering "reopen the period" would
 * be worse: an unlock is keyed on a covering deadline, so a floor-locked date that has none provably
 * cannot be reopened at all, and the treasurer would chase a remedy that does not exist.
 *
 * The other trade is accepted: a filed period whose rows were never uploaded (an email-filed or
 * attest-only jurisdiction) loses the more specific "already on file in …" wording. The row is
 * read-only either way. Naming the filing there would need a "a filed report covers this date"
 * signal on the row (ADR-081's predicate, batched), which is deliberately not built for this.
 *
 * The frontend owns this user-facing lock copy at the render boundary — the shared backend refusal
 * message is deliberately jurisdiction-NEUTRAL. The destination comes from `usePortalUploadModel()`,
 * the one place the filing-model boundary is resolved.
 *
 * The banner is only the read-only LOCK signal; it deliberately does NOT restate the "book the
 * difference / add-only / amend" correction rule. That guidance stays owned by the fee-row-specific
 * "Statement differs?" detail line and by the backend's neutral refusal fallback.
 */
export function rowLockCopy(
  model: FilingDestinationModel,
  { uploadLocked, reopenable = false }: { uploadLocked: boolean; reopenable?: boolean },
): RowLockCopy {
  if (!uploadLocked && reopenable) {
    return {
      label: 'Locked',
      detail: "This period's report is filed, so this row is locked. To change it, reopen the period from its report.",
      reopenLinkLabel: 'Open the report',
    };
  }
  if (!uploadLocked) {
    // "Locked" matches the badge the report surfaces already use for this same state
    // (FilingLockCopy.LockedBadge) — one word for one thing across the app.
    return { label: 'Locked', detail: "This reporting period is locked — you can't edit this here." };
  }

  // Neutral when there is no nameable portal on this shared surface: a non-portal filer, OR federal
  // (certifyAtGenerate) — which files at efo.fec.gov and can't be named "FEC" here. Naming nothing is
  // correct for federal; the "already on file" lock signal alone carries the meaning.
  if (!model.isPortalUpload || model.certifyAtGenerate) {
    return { label: 'Filed', detail: 'This is already on file — you never edit it.' };
  }
  const destination = filingDestinationPhrase(model);
  return {
    label: 'Filed',
    detail: `This is already on file in ${destination} — you never edit it.`,
  };
}
